import type { FilterSchema } from '@/shared/components/filter/validation'
import type { FilterRuleModel } from '@/shared/generated'
import type { WidgetPeriods } from '../types'

import { any, last, pipe, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useBIAnalyticQuery, useBiImpactQuery, zipSeries } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'

import { Operators } from '@/shared/components/query-builder/constants'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { cn } from '@/shared/lib/utils'
import { formatMoney, formatNumber, NumberFormats } from '@/shared/tools/locale'

import { Widget, WidgetContent, WidgetHeader } from '../../Widget'
import WidgetEmptyState from '../../WidgetEmptyState'
import WidgetErrorState from '../../WidgetErrorState'

const REVENUE_METRIC = 'revenue'
const ASSORTMENT_METRIC = 'assortment'
const BI_IMPACT_FILTER: FilterRuleModel = {
	name: 'acceptance_status',
	operation: Operators.IN,
	value: ['accepted', 'changed_manually'],
}

export default function NumericRevenueBoostWidget({
	commonFilters,
	commonPeriods,
	className,
}: {
	commonFilters: FilterSchema[]
	commonPeriods: WidgetPeriods
	className?: string
}) {
	const { t } = useTranslation()

	const filters = [BI_IMPACT_FILTER, ...commonFilters]
	const currency = useSelectedCurrency()
	const biRevenueImpactQuery = useBiImpactQuery(
		{
			currency,
			label: 'NumericRevenueBoost',
			start_date: commonPeriods?.main?.start_date,
			end_date: commonPeriods?.main?.end_date,
			metrics: [REVENUE_METRIC],
			filters,
		},
		{
			enabled: !!commonPeriods?.main,
			select: getBiRevenueImpact,
		}
	)
	const biAssortmentQuery = useBIAnalyticQuery(
		{
			currency,
			label: 'NumericRevenueBoost',
			start_date: commonPeriods?.main?.start_date,
			end_date: commonPeriods?.main?.end_date,
			metrics: [ASSORTMENT_METRIC],
			date_aggregation: null,
			filters,
		},
		{
			select: getBiAssortment,
			enabled: !!commonPeriods?.main,
		}
	)
	const isLoading = any(prop('isLoading'), [
		biAssortmentQuery,
		biRevenueImpactQuery,
	])
	const isError = any(prop('isError'), [
		biAssortmentQuery,
		biRevenueImpactQuery,
	])
	const data = biAssortmentQuery?.data && biRevenueImpactQuery?.data

	return (
		<Widget className={cn('relative truncate', className)}>
			<WidgetHeader
				title={t('widget.revenue_boost.title', {
					defaultValue: 'Revenue Boost',
				})}
				tooltip={t('widget.revenue_boost.tooltip', {
					defaultValue: 'widget.revenue_boost.tooltip',
				})}
				subtitle={t('widget.revenue_boost.description', {
					defaultValue: 'widget.revenue_boost.description',
				})}
			/>
			<WidgetErrorState isTinted={isError} size='small' />
			<WidgetContent className='truncate'>
				{isLoading && (
					<div className='space-y-1'>
						<Skeleton className='col-span-2 h-6 w-24' />
						<Skeleton className='col-span-2 h-8 w-32' />
					</div>
				)}
				{!!data && !isLoading && (
					<div className='space-y-1'>
						<p className='text-xs text-muted-foreground'>
							{formatNumber(biAssortmentQuery?.data)}{' '}
							{t('general_skus', { defaultValue: 'SKUs' })}
						</p>
						<p className='truncate text-3xl font-semibold'>
							{formatMoney(
								biRevenueImpactQuery?.data,
								currency,
								NumberFormats.Compact
							)}
						</p>
					</div>
				)}
				{!isLoading && !isError && !data && <WidgetEmptyState size='small' />}
			</WidgetContent>
		</Widget>
	)
}
const getBiRevenueImpact = pipe(
	(data) => zipSeries(data?.meta, data?.data),
	last,
	prop(REVENUE_METRIC)
)

const getBiAssortment = pipe(
	(data) => zipSeries(data?.meta, data?.data),
	last,
	prop(ASSORTMENT_METRIC)
)
